import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Container,
  Grid,
  Stack,
  Chip,
  Avatar,
  Button,
  Alert,
  Skeleton,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import {
  MdAccountCircle,
  MdArrowBack,
  MdEmail,
  MdBadge,
  MdPhone,
  MdCalendarToday,
  MdSchool,
  MdWork,
  MdMap,
  MdCleaningServices,
  MdVerifiedUser,
  MdShield,
  MdSchedule,
  MdCheckCircle,
} from "react-icons/md";
import axios from "../utils/api";
import { getClientEmail } from "../utils/getClientEmail";
import { getRoleLabel, isAdminProfileIncomplete } from "../constants/adminRoles";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulseRing = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.35); }
  50% { box-shadow: 0 0 0 12px rgba(99, 102, 241, 0); }
`;

function parseAdminProfile(data) {
  if (!data || typeof data !== "object") return {};
  const inner = data.user ?? data.data ?? data.admin ?? data.record;
  const src = inner && typeof inner === "object" ? { ...data, ...inner } : data;
  return {
    name: src.name ?? src.userName ?? src.fullName ?? src.displayName ?? "",
    email: src.email ?? "",
    role: src.role ?? "",
    employeeId: src.employeeId ?? src.EmployeeId ?? "",
    phoneNumber: src.phoneNumber ?? "",
    dob: src.dob ?? "",
    dateOfJoin: src.dateOfJoin ?? "",
    qualification: src.qualification ?? "",
    status: src.status ?? "active",
    manualMapping: src.manualMapping ?? "enabled",
    objectDisinfection: src.objectDisinfection ?? "enabled",
  };
}

function getInitials(name) {
  if (!name) return "";
  return name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function formatDate(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function hasValue(v) {
  return v != null && String(v).trim() !== "";
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function getCalendarTenure(startDate, endDate) {
  let years = endDate.getFullYear() - startDate.getFullYear();
  let months = endDate.getMonth() - startDate.getMonth();
  let days = endDate.getDate() - startDate.getDate();

  if (days < 0) {
    months -= 1;
    days += new Date(endDate.getFullYear(), endDate.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return { years, months, days };
}

function formatTenureYMD({ years, months, days }) {
  return `${pad2(years)}/${pad2(months)}/${pad2(days)}`;
}

function getTenureFromJoin(dateString) {
  if (!hasValue(dateString)) {
    return {
      days: null,
      formattedYMD: "—",
      summary: "—",
      joinDate: null,
      workingLabel: "—",
    };
  }

  const join = new Date(dateString);
  if (Number.isNaN(join.getTime())) {
    return {
      days: null,
      formattedYMD: "—",
      summary: "—",
      joinDate: formatDate(dateString),
      workingLabel: "—",
    };
  }

  const start = new Date(join.getFullYear(), join.getMonth(), join.getDate());
  const today = new Date();
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.max(0, Math.floor((end - start) / (1000 * 60 * 60 * 24)));
  const joinDate = formatDate(dateString);
  const ymd = getCalendarTenure(start, end);
  const formattedYMD = formatTenureYMD(ymd);

  let summary;
  let workingLabel;
  if (days === 0) {
    summary = "00/00/00";
    workingLabel = "Started today";
  } else {
    summary = formattedYMD;
    workingLabel = `Working for ${formattedYMD} (${days.toLocaleString()} days)`;
  }

  return { days, formattedYMD, ymd, summary, joinDate, workingLabel };
}

function isActiveStatus(value, activeLabel = "enabled") {
  const v = String(value || activeLabel).toLowerCase();
  return v === "enabled" || v === "active";
}

function ProfileSkeleton() {
  return (
    <Box sx={sx.root}>
      <Box sx={sx.heroWrap}>
        <Container maxWidth="lg" sx={sx.heroInner}>
          <Skeleton variant="rounded" width={88} height={36} sx={{ bgcolor: "rgba(255,255,255,0.08)", mb: 3 }} />
          <Skeleton variant="rounded" width={160} height={28} sx={{ bgcolor: "rgba(255,255,255,0.1)", mb: 2 }} />
          <Skeleton variant="text" width="40%" height={48} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
        </Container>
      </Box>
      <Container maxWidth="lg" sx={{ mt: -6, position: "relative", zIndex: 2, pb: 8 }}>
        <Box sx={sx.identityCard}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Skeleton variant="circular" width={72} height={72} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="55%" height={32} />
                <Skeleton variant="text" width="40%" height={22} />
              </Box>
            </Stack>
            <Skeleton variant="rounded" height={88} sx={{ borderRadius: "14px" }} />
          </Stack>
        </Box>
        <Grid container spacing={2.5} sx={{ mt: 3 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <Skeleton variant="rounded" height={140} sx={{ borderRadius: "20px" }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

function TenureYMD({ ymd, accent = "#14b8a6" }) {
  if (!ymd) return <Typography sx={sx.metricValue}>—</Typography>;

  const segments = [
    { value: pad2(ymd.years), unit: "yr" },
    { value: pad2(ymd.months), unit: "mo" },
    { value: pad2(ymd.days), unit: "dd" },
  ];

  return (
    <Stack direction="row" spacing={0.75} alignItems="flex-end">
      {segments.map((seg, i) => (
        <React.Fragment key={seg.unit}>
          {i > 0 && (
            <Typography sx={{ ...sx.tenureSlash, color: accent }}>/</Typography>
          )}
          <Box sx={sx.tenureUnit}>
            <Typography sx={{ ...sx.tenureNum, color: accent }}>{seg.value}</Typography>
            <Typography sx={sx.tenureUnitLabel}>{seg.unit}</Typography>
          </Box>
        </React.Fragment>
      ))}
    </Stack>
  );
}

function ProfileMetrics({ employeeId, tenure, active }) {
  return (
    <Box sx={sx.metricsBar}>
      <Box sx={sx.metricCell}>
        <Box sx={{ ...sx.metricIcon, background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)" }}>
          <MdBadge size={17} color="#fff" />
        </Box>
        <Box sx={sx.metricBody}>
          <Typography sx={sx.metricLabel}>Employee ID</Typography>
          <Typography sx={sx.metricValue}>{employeeId || "—"}</Typography>
        </Box>
      </Box>

      <Box sx={sx.metricDivider} aria-hidden />

      <Box sx={{ ...sx.metricCell, flex: { xs: 1.2, md: 1.35 } }}>
        <Box sx={{ ...sx.metricIcon, background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)" }}>
          <MdSchedule size={17} color="#fff" />
        </Box>
        <Box sx={sx.metricBody}>
          <Typography sx={sx.metricLabel}>Tenure</Typography>
          <TenureYMD ymd={tenure.ymd} />
          {tenure.days != null && tenure.joinDate && (
            <Typography sx={sx.metricMeta}>
              {tenure.days.toLocaleString()} days · since {tenure.joinDate}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={sx.metricDivider} aria-hidden />

      <Box sx={sx.metricCell}>
        <Box
          sx={{
            ...sx.metricIcon,
            background: active
              ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
              : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
          }}
        >
          <MdCheckCircle size={17} color="#fff" />
        </Box>
        <Box sx={sx.metricBody}>
          <Typography sx={sx.metricLabel}>Account</Typography>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                bgcolor: active ? "#10b981" : "#ef4444",
                boxShadow: active
                  ? "0 0 0 3px rgba(16, 185, 129, 0.2)"
                  : "0 0 0 3px rgba(239, 68, 68, 0.15)",
              }}
            />
            <Typography
              sx={{
                ...sx.metricValue,
                color: active ? "#059669" : "#dc2626",
              }}
            >
              {active ? "Active" : "Inactive"}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

function InfoTile({ icon: Icon, label, value, accent, delay = 0, children }) {
  return (
    <Box
      sx={{
        ...sx.infoTile,
        animation: `${fadeUp} 0.55s ease-out ${delay}s both`,
      }}
    >
      <Box sx={{ ...sx.tileIcon, background: accent }}>
        <Icon size={22} color="#fff" />
      </Box>
      <Typography variant="overline" sx={sx.tileLabel}>
        {label}
      </Typography>
      {children ?? (
        <Typography variant="h6" sx={sx.tileValue}>
          {value || "—"}
        </Typography>
      )}
    </Box>
  );
}

function PermissionTile({ icon: Icon, label, value, description, accent, delay = 0 }) {
  const active = isActiveStatus(value);
  return (
    <Box
      sx={{
        ...sx.permissionTile,
        animation: `${fadeUp} 0.55s ease-out ${delay}s both`,
        borderColor: active ? "rgba(16, 185, 129, 0.25)" : "rgba(239, 68, 68, 0.2)",
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box sx={{ ...sx.tileIcon, background: accent, width: 48, height: 48, borderRadius: "14px" }}>
          <Icon size={22} color="#fff" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="subtitle1" sx={sx.permissionTitle}>
              {label}
            </Typography>
            <Chip
              size="small"
              label={value || "enabled"}
              sx={{
                height: 24,
                fontWeight: 700,
                fontSize: "0.7rem",
                textTransform: "capitalize",
                bgcolor: active ? "rgba(16, 185, 129, 0.12)" : "rgba(239, 68, 68, 0.1)",
                color: active ? "#059669" : "#dc2626",
                border: `1px solid ${active ? "rgba(16, 185, 129, 0.25)" : "rgba(239, 68, 68, 0.2)"}`,
              }}
            />
          </Stack>
          <Typography variant="body2" sx={sx.permissionDesc}>
            {description}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const email = getClientEmail();
        if (!email) {
          setError("Unable to load profile. Please sign in again.");
          setLoading(false);
          return;
        }
        const response = await axios.get(
          `getemail-admin-details/${encodeURIComponent(email)}`
        );
        setProfile(parseAdminProfile(response.data));
      } catch (err) {
        console.error("Error fetching profile:", err);
        setError("Failed to load profile details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  if (loading) return <ProfileSkeleton />;

  if (error) {
    return (
      <Box sx={sx.root}>
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: "16px",
              border: "1px solid rgba(239, 68, 68, 0.2)",
              boxShadow: "0 8px 32px rgba(239, 68, 68, 0.08)",
            }}
          >
            {error}
          </Alert>
          <Button
            startIcon={<MdArrowBack />}
            onClick={() => navigate(-1)}
            sx={sx.backBtnLight}
          >
            Go back
          </Button>
        </Container>
      </Box>
    );
  }

  const incomplete = isAdminProfileIncomplete(profile);
  const tenure = getTenureFromJoin(profile.dateOfJoin);
  const dob = formatDate(profile.dob);
  const active = isActiveStatus(profile.status, "active");

  return (
    <Box sx={sx.root}>
      {/* Hero */}
      <Box sx={sx.heroWrap}>
        <Box sx={sx.heroGlowA} aria-hidden />
        <Box sx={sx.heroGlowB} aria-hidden />
        <Box sx={sx.heroGrid} aria-hidden />

        <Container maxWidth="lg" sx={sx.heroInner}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Button
              startIcon={<MdArrowBack size={18} />}
              onClick={() => navigate(-1)}
              sx={{ ...sx.backBtn, mb: 0 }}
            >
              Back
            </Button>
            <Chip
              icon={<MdVerifiedUser style={{ fontSize: 16 }} />}
              label="Admin account"
              sx={{
                ...sx.heroChip,
                mb: 0,
                animation: `${fadeUp} 0.6s ease-out both`,
              }}
            />
          </Stack>

          <Typography
            component="h1"
            sx={{
              ...sx.heroTitle,
              animation: `${fadeUp} 0.65s ease-out 0.06s both`,
            }}
          >
            Your Profile
          </Typography>
          <Typography
            sx={{
              ...sx.heroLead,
              animation: `${fadeUp} 0.65s ease-out 0.12s both`,
            }}
          >
            A complete view of your identity, credentials, and portal permissions.
          </Typography>
        </Container>
      </Box>

      {/* Identity card */}
      <Container maxWidth="lg" sx={sx.mainContainer}>
        <Box
          sx={{
            ...sx.identityCard,
            animation: `${fadeUp} 0.7s ease-out 0.1s both`,
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 2, sm: 2.5 }}
              alignItems={{ xs: "center", sm: "center" }}
            >
              <Box sx={sx.avatarWrap}>
                <Avatar sx={sx.avatar}>
                  {profile.name ? getInitials(profile.name) : <MdAccountCircle size={36} />}
                </Avatar>
                <Box sx={sx.avatarRing} aria-hidden />
              </Box>

              <Box sx={{ flex: 1, width: "100%", textAlign: { xs: "center", sm: "left" }, minWidth: 0 }}>
                <Typography variant="h5" sx={sx.profileName}>
                  {profile.name || "Admin User"}
                </Typography>
                <Typography sx={sx.profileEmail}>{profile.email}</Typography>

                <Stack
                  direction="row"
                  spacing={0.75}
                  flexWrap="wrap"
                  justifyContent={{ xs: "center", sm: "flex-start" }}
                  sx={{ mt: 1 }}
                >
                  <Chip
                    size="small"
                    icon={<MdShield style={{ fontSize: 14 }} />}
                    label={getRoleLabel(profile.role)}
                    sx={sx.roleChip}
                  />
                  <Chip
                    size="small"
                    label={profile.status || "active"}
                    sx={{
                      ...sx.statusChip,
                      bgcolor: active ? "rgba(16, 185, 129, 0.12)" : "rgba(239, 68, 68, 0.1)",
                      color: active ? "#059669" : "#dc2626",
                      border: `1px solid ${active ? "rgba(16, 185, 129, 0.25)" : "rgba(239, 68, 68, 0.2)"}`,
                    }}
                  />
                  {incomplete && (
                    <Chip size="small" label="Profile incomplete" sx={sx.warnChip} />
                  )}
                </Stack>
              </Box>
            </Stack>

            <ProfileMetrics
              employeeId={profile.employeeId}
              tenure={tenure}
              active={active}
            />
          </Stack>
        </Box>

        {/* Personal details */}
        <Box sx={{ mt: 5, mb: 2.5 }}>
          <Typography variant="overline" sx={sx.sectionEyebrow}>
            Personal
          </Typography>
          <Typography variant="h5" sx={sx.sectionTitle}>
            Identity & contact
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} lg={4}>
            <InfoTile
              icon={MdAccountCircle}
              label="Full name"
              value={profile.name}
              accent="linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
              delay={0.14}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <InfoTile
              icon={MdEmail}
              label="Email address"
              value={profile.email}
              accent="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
              delay={0.18}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <InfoTile
              icon={MdBadge}
              label="Employee ID"
              value={profile.employeeId}
              accent="linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
              delay={0.22}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <InfoTile
              icon={MdPhone}
              label="Phone number"
              value={profile.phoneNumber}
              accent="linear-gradient(135deg, #10b981 0%, #059669 100%)"
              delay={0.26}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <InfoTile
              icon={MdCalendarToday}
              label="Date of birth"
              value={dob}
              accent="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
              delay={0.3}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <InfoTile
              icon={MdSchool}
              label="Qualification"
              value={profile.qualification}
              accent="linear-gradient(135deg, #ec4899 0%, #db2777 100%)"
              delay={0.34}
            />
          </Grid>
        </Grid>

        {/* Work & permissions */}
        <Box sx={{ mt: 6, mb: 2.5 }}>
          <Typography variant="overline" sx={sx.sectionEyebrow}>
            Organization
          </Typography>
          <Typography variant="h5" sx={sx.sectionTitle}>
            Role & access controls
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <InfoTile
              icon={MdWork}
              label="Role"
              value={getRoleLabel(profile.role)}
              accent="linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)"
              delay={0.38}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoTile
              icon={MdCalendarToday}
              label="Date of join"
              accent="linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)"
              delay={0.42}
            >
              <Typography variant="h6" sx={sx.tileValue}>
                {tenure.workingLabel}
              </Typography>
              {tenure.formattedYMD && tenure.formattedYMD !== "—" && tenure.ymd && (
                <Box sx={{ mt: 0.75 }}>
                  <TenureYMD ymd={tenure.ymd} />
                </Box>
              )}
              {tenure.joinDate && (
                <Typography variant="body2" sx={{ ...sx.tileSubvalue, mt: 0.75 }}>
                  {tenure.days != null
                    ? `${tenure.days.toLocaleString()} days · joined ${tenure.joinDate}`
                    : `Joined on ${tenure.joinDate}`}
                </Typography>
              )}
            </InfoTile>
          </Grid>
          <Grid item xs={12} md={6}>
            <PermissionTile
              icon={MdMap}
              label="Manual mapping"
              value={profile.manualMapping}
              description="Controls whether manual map editing and room mapping tools are available for your account."
              accent="linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
              delay={0.46}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PermissionTile
              icon={MdCleaningServices}
              label="Object disinfection"
              value={profile.objectDisinfection}
              description="Enables object-focused disinfection workflows and related robot operation modes."
              accent="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
              delay={0.5}
            />
          </Grid>
        </Grid>
      </Container>

      <Box sx={sx.bottomBand}>
        <Container maxWidth="md">
          <Typography variant="body2" sx={sx.bottomText}>
            Profile details are managed by your organization&apos;s administrators. Contact HR
            if any information needs to be updated.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

const sx = {
  root: {
    minHeight: "calc(100vh - 72px)",
    bgcolor: "#f8fafc",
  },
  heroWrap: {
    position: "relative",
    overflow: "hidden",
    pt: { xs: 3, md: 4 },
    pb: { xs: 10, md: 12 },
    background: "linear-gradient(165deg, #0f172a 0%, #1e1b4b 42%, #312e81 100%)",
  },
  heroGlowA: {
    position: "absolute",
    top: "-15%",
    right: "-8%",
    width: { xs: "75%", md: "42%" },
    height: "85%",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(99, 102, 241, 0.5) 0%, transparent 70%)",
    filter: "blur(48px)",
    pointerEvents: "none",
  },
  heroGlowB: {
    position: "absolute",
    bottom: "-20%",
    left: "-12%",
    width: "50%",
    height: "75%",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(236, 72, 153, 0.28) 0%, transparent 65%)",
    filter: "blur(52px)",
    pointerEvents: "none",
  },
  heroGrid: {
    position: "absolute",
    inset: 0,
    opacity: 0.06,
    backgroundImage: `
      linear-gradient(rgba(255,255,255,0.14) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.14) 1px, transparent 1px)
    `,
    backgroundSize: "44px 44px",
    pointerEvents: "none",
  },
  heroInner: {
    position: "relative",
    zIndex: 1,
  },
  backBtn: {
    color: "rgba(255,255,255,0.88)",
    textTransform: "none",
    fontWeight: 600,
    mb: 3,
    borderRadius: "999px",
    px: 2,
    border: "1px solid rgba(255,255,255,0.14)",
    bgcolor: "rgba(255,255,255,0.06)",
    backdropFilter: "blur(8px)",
    "&:hover": { bgcolor: "rgba(255,255,255,0.12)", borderColor: "rgba(255,255,255,0.22)" },
  },
  backBtnLight: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "12px",
    px: 2.5,
  },
  heroChip: {
    mb: 2,
    bgcolor: "rgba(255,255,255,0.1)",
    color: "#e2e8f0",
    border: "1px solid rgba(255,255,255,0.18)",
    fontWeight: 600,
    letterSpacing: "0.04em",
    "& .MuiChip-icon": { color: "#a5b4fc" },
  },
  heroTitle: {
    m: 0,
    mb: 1.5,
    fontSize: { xs: "2rem", sm: "2.5rem", md: "2.85rem" },
    fontWeight: 800,
    letterSpacing: "-0.04em",
    lineHeight: 1.1,
    color: "#fff",
  },
  heroLead: {
    color: "rgba(226, 232, 240, 0.9)",
    lineHeight: 1.75,
    maxWidth: 520,
    fontSize: { xs: "1rem", md: "1.0625rem" },
  },
  mainContainer: {
    mt: { xs: -5, md: -6 },
    position: "relative",
    zIndex: 2,
    pb: { xs: 6, md: 8 },
  },
  identityCard: {
    p: { xs: 2, md: 2.5 },
    borderRadius: "18px",
    background: "rgba(255, 255, 255, 0.96)",
    border: "1px solid rgba(15, 23, 42, 0.06)",
    boxShadow:
      "0 4px 6px -1px rgba(15, 23, 42, 0.05), 0 20px 40px -12px rgba(15, 23, 42, 0.1)",
    backdropFilter: "blur(12px)",
  },
  avatarWrap: {
    position: "relative",
    flexShrink: 0,
  },
  avatar: {
    width: { xs: 64, md: 72 },
    height: { xs: 64, md: 72 },
    fontSize: { xs: "1.25rem", md: "1.4rem" },
    fontWeight: 800,
    background: "linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)",
    border: "3px solid #fff",
    boxShadow: "0 8px 24px rgba(99, 102, 241, 0.3)",
    position: "relative",
    zIndex: 1,
    animation: `${pulseRing} 2.8s ease-in-out infinite`,
  },
  avatarRing: {
    position: "absolute",
    inset: -6,
    borderRadius: "50%",
    background: "linear-gradient(135deg, rgba(99,102,241,0.35), rgba(236,72,153,0.25))",
    filter: "blur(5px)",
    zIndex: 0,
  },
  profileName: {
    fontWeight: 800,
    color: "#0f172a",
    letterSpacing: "-0.02em",
    fontSize: { xs: "1.2rem", md: "1.35rem" },
    lineHeight: 1.25,
  },
  profileEmail: {
    color: "#64748b",
    fontSize: { xs: "0.82rem", md: "0.875rem" },
    mt: 0.25,
    wordBreak: "break-word",
  },
  roleChip: {
    fontWeight: 600,
    height: 26,
    fontSize: "0.72rem",
    bgcolor: "rgba(99, 102, 241, 0.1)",
    color: "#4f46e5",
    border: "1px solid rgba(99, 102, 241, 0.2)",
    "& .MuiChip-icon": { color: "#6366f1" },
  },
  statusChip: {
    fontWeight: 600,
    height: 26,
    fontSize: "0.72rem",
    textTransform: "capitalize",
  },
  warnChip: {
    fontWeight: 600,
    height: 26,
    fontSize: "0.72rem",
    bgcolor: "rgba(245, 158, 11, 0.12)",
    color: "#b45309",
    border: "1px solid rgba(245, 158, 11, 0.25)",
  },
  metricsBar: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    alignItems: "stretch",
    borderRadius: "14px",
    background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
  },
  metricCell: {
    flex: 1,
    display: "flex",
    alignItems: "flex-start",
    gap: 1.25,
    px: { xs: 1.75, md: 2 },
    py: { xs: 1.5, md: 1.75 },
    minWidth: 0,
    borderBottom: { xs: "1px solid #e2e8f0", md: "none" },
    "&:last-child": { borderBottom: "none" },
  },
  metricDivider: {
    display: { xs: "none", md: "block" },
    width: "1px",
    alignSelf: "stretch",
    bgcolor: "#e2e8f0",
    my: 1.25,
  },
  metricIcon: {
    width: 36,
    height: 36,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.1)",
  },
  metricBody: {
    minWidth: 0,
    flex: 1,
  },
  metricLabel: {
    color: "#94a3b8",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontSize: "0.62rem",
    lineHeight: 1.2,
    mb: 0.35,
  },
  metricValue: {
    color: "#0f172a",
    fontWeight: 700,
    fontSize: "0.9rem",
    lineHeight: 1.3,
    wordBreak: "break-word",
  },
  metricMeta: {
    mt: 0.5,
    color: "#64748b",
    fontSize: "0.68rem",
    fontWeight: 500,
    lineHeight: 1.35,
  },
  tenureUnit: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: 22,
  },
  tenureNum: {
    fontWeight: 800,
    fontSize: "1rem",
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
  },
  tenureUnitLabel: {
    color: "#94a3b8",
    fontSize: "0.58rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    mt: 0.2,
  },
  tenureSlash: {
    fontWeight: 300,
    fontSize: "0.95rem",
    lineHeight: 1,
    pb: 0.6,
    opacity: 0.7,
  },
  sectionEyebrow: {
    color: "#6366f1",
    fontWeight: 800,
    letterSpacing: "0.14em",
    fontSize: "0.7rem",
    display: "block",
    mb: 0.5,
  },
  sectionTitle: {
    fontWeight: 800,
    color: "#0f172a",
    letterSpacing: "-0.03em",
    fontSize: { xs: "1.35rem", md: "1.5rem" },
  },
  infoTile: {
    height: "100%",
    p: { xs: 2.25, md: 2.5 },
    borderRadius: "20px",
    background: "rgba(255, 255, 255, 0.94)",
    border: "1px solid rgba(15, 23, 42, 0.06)",
    boxShadow: "0 4px 6px -1px rgba(15, 23, 42, 0.05), 0 20px 40px -12px rgba(15, 23, 42, 0.08)",
    transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s ease",
    position: "relative",
    overflow: "hidden",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "3px",
      background: "linear-gradient(90deg, #6366f1, #a855f7, #ec4899)",
      opacity: 0,
      transition: "opacity 0.3s ease",
    },
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 12px 28px -6px rgba(15, 23, 42, 0.12), 0 28px 56px -16px rgba(79, 70, 229, 0.14)",
      "&::after": { opacity: 1 },
    },
  },
  tileIcon: {
    width: 44,
    height: 44,
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mb: 1.75,
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)",
  },
  tileLabel: {
    color: "#94a3b8",
    fontWeight: 700,
    letterSpacing: "0.1em",
    fontSize: "0.68rem",
    display: "block",
    mb: 0.75,
  },
  tileValue: {
    color: "#0f172a",
    fontWeight: 700,
    fontSize: { xs: "1.05rem", md: "1.125rem" },
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  tileSubvalue: {
    color: "#64748b",
    mt: 0.5,
    fontWeight: 500,
    fontSize: "0.875rem",
  },
  permissionTile: {
    height: "100%",
    p: { xs: 2.25, md: 2.5 },
    borderRadius: "20px",
    background: "rgba(255, 255, 255, 0.94)",
    border: "1px solid rgba(15, 23, 42, 0.06)",
    boxShadow: "0 4px 6px -1px rgba(15, 23, 42, 0.05), 0 20px 40px -12px rgba(15, 23, 42, 0.08)",
    transition: "transform 0.35s ease, box-shadow 0.35s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 28px -6px rgba(15, 23, 42, 0.1)",
    },
  },
  permissionTitle: {
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "-0.01em",
  },
  permissionDesc: {
    color: "#64748b",
    lineHeight: 1.65,
    mt: 0.75,
    fontSize: "0.875rem",
  },
  bottomBand: {
    py: 4,
    borderTop: "1px solid rgba(15, 23, 42, 0.06)",
    background: "linear-gradient(180deg, #f1f5f9 0%, #f8fafc 100%)",
  },
  bottomText: {
    color: "#64748b",
    textAlign: "center",
    lineHeight: 1.75,
    maxWidth: 560,
    mx: "auto",
    fontSize: "0.9rem",
  },
};
